// First-party persistence of ad click IDs (?fbclid / ?gclid) from the landing
// URL. The vendor cookies (_fbc via fbevents.js, _gcl_* via GTM's Conversion
// Linker) are only written after marketing consent is granted - if the user
// consents after navigating away from the landing URL, the click id is gone.
// Stashing it here lets checkout attribution survive that gap; it is only ever
// SENT with the order when marketing consent was given (ACEB-220).

const FBCLID_KEY = 'aceb_fbclid';
const GCLID_KEY = 'aceb_gclid';

// Meta attributes clicks within 7 days; Google Ads click conversions within 90.
const FBCLID_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const GCLID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

// Click IDs are URL-safe tokens; cap length and charset like referral codes.
const isPlausibleClickId = (value: string): boolean =>
  /^[A-Za-z0-9_.-]{1,512}$/.test(value);

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, JSON.stringify({ v: value, ts: Date.now() }));
  } catch {
    // storage unavailable (private mode, quota) - attribution falls back to URL
  }
}

function read(key: string, ttlMs: number): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { v?: string; ts?: number };
    if (
      typeof parsed.v !== 'string'
      || typeof parsed.ts !== 'number'
      || Date.now() - parsed.ts > ttlMs
      || !isPlausibleClickId(parsed.v)
    ) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.v;
  } catch {
    return null;
  }
}

export function captureClickIdsFromUrl(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  const gclid = params.get('gclid');
  if (fbclid && isPlausibleClickId(fbclid)) store(FBCLID_KEY, fbclid);
  if (gclid && isPlausibleClickId(gclid)) store(GCLID_KEY, gclid);
}

export function getStoredFbclid(): string | null {
  if (typeof window === 'undefined') return null;
  return read(FBCLID_KEY, FBCLID_TTL_MS);
}

export function getStoredGclid(): string | null {
  if (typeof window === 'undefined') return null;
  return read(GCLID_KEY, GCLID_TTL_MS);
}

/** Mirrors the cookie scrub on marketing-consent withdrawal. */
export function clearStoredClickIds(): void {
  try {
    localStorage.removeItem(FBCLID_KEY);
    localStorage.removeItem(GCLID_KEY);
  } catch {
    // nothing to clear
  }
}
